import React, { useEffect, useRef } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { Feature, Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import "ol/ol.css";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { LineString, MultiLineString, Point } from "ol/geom";
import { Fill, Stroke, Style, Text } from "ol/style";
import { fromLonLat } from "ol/proj.js";
import IconStyle from "ol/style/Icon";
import locationMarker from "../../static/map-marker.svg";
import { categoryToRiskLevel, impactLevelInfo } from "../../app/riskLevel";
import { Chip } from "@mui/material";
import { ADVISORY_ROUTE_TEXT, NEW_FORECAST_MESSAGE } from "../UserVisibleStrings";
import { RouteRequestInput, RouteRequestInputType, Trip } from "../../generated/gpb/api/route";
import { resubmitRouteRequestInput } from "../trips/form/TripFormTypeConversions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/sharp-duotone-light-svg-icons";
import { getContrastColor } from "./bottombar/LegendStyle";
import { RiskCategory } from "../../generated/gpb/api/risk";
import ImpactIcon from "./ImpactIcon";
import { useAddTripMutation } from "../../services/routing";
import { TripId } from "../../generated/gpb/api/id";
import { isPastStartDate } from "../trips/tripUtils";
import { openEdit } from "../../features/Drawer/drawerSlice";
import { getRouteBoundingBox } from "./ThumbnailMapUtils";
import { setSelectionState } from "../../features/riskViewer/riskViewerSlice";
import { useAppDispatch } from "../../app/hooks";
import { ADVISORY_COLOR } from "../trips/form/BorderWithLabel";

// Props for the ThumbnailMap component
type ThumbnailMapProps = {
    trip: Trip,
    resetToPage: ( event: React.ChangeEvent<unknown>, value: number ) => void,
};

function isValidExtent( extent: Array<number> )
{
    return extent.map( each => isFinite( each ) ).reduce( ( each, sum ) => each && sum );
}

/**
 * We've seen a spike in our mapbox queries since using the thumbnails.
 * To conserve costs, we're switching thumbnails to openlayers.
 *
 * Note that also when we started showing all variants of trips, mapbox was displaying errors
 * (cause yet to be determined), so this solves two issues at once.
 * @param trip
 * @param routeIndex
 * @param resetToPage
 * @constructor
 */
export default function ThumbnailMap( { trip, resetToPage }: Readonly<ThumbnailMapProps> )
{
    const mapRef = useRef<HTMLDivElement>( null );
    const waypoints = trip.routeRequestInput?.waypoints;
    const risk = trip.routeRequestInput?.acceptableRiskLevelCeiling;
    const isAdvisory = trip.routeRequestInput?.requestType === RouteRequestInputType.ADVISORY;
    const shouldShowGenerateNewTrip = (
        trip.lastModifiedDate !== undefined &&
        trip.Id?.hexString !== undefined &&
        trip.routeRequestInput !== undefined
    ) ? ( Date.now() - trip.lastModifiedDate.getTime() ) >= 6 * 60 * 60 * 1000 //6 hours in milliseconds
        : false;
    const [ addTrip ] = useAddTripMutation();
    const dispatch = useAppDispatch();

    const resubmitRequest = async ( tripId: TripId | undefined, tripName: string, data: RouteRequestInput | undefined ) =>
    {
        if ( tripId !== undefined && data !== undefined )
        {
            await addTrip( {
                parentTripId: tripId,
                tripName,
                routeRequestInput: data,
            } );
        }
    };

    const pointFeatures: Array<Feature> = [];
    const coordList: Array<LineString> = [];
    let prior = undefined;
    for ( let i = 0; i < ( waypoints?.length ?? 0 ); i++ )
    {
        if ( waypoints !== undefined &&
            waypoints[i]?.location?.location?.longitude !== undefined &&
            waypoints[i]?.location?.location?.latitude !== undefined )
        {
            const coord = fromLonLat( [
                waypoints[i]?.location?.location?.longitude ?? 0,
                waypoints[i]?.location?.location?.latitude ?? 0
            ] );
            pointFeatures.push( new Feature( {
                geometry: new Point( coord ),
                properties: { name: waypoints[i].location?.locationName, index: i }
            } ) );
            if ( prior !== undefined )
            {
                coordList.push( new LineString( [ prior, coord ] ) );
            }
            prior = coord;
        }
    }
    const lineFeature = new Feature( {
        geometry: new MultiLineString( coordList ),
    } );
    const lineStyle = new Style( {
        stroke: new Stroke( {
            color: "grey",
            width: 2,
            lineDash: [ 4, 4, 4 ],
            lineCap: "butt"
        } )
    } );

    const pointSource = new VectorSource( {
        features: pointFeatures
    } );

    const pointLayer = new VectorLayer( {
        source: pointSource,
        style: function ( feature )
        {
            const index = "" + ( feature.getPropertiesInternal()?.properties["index"] + 1 );
            return new Style( {
                image: new IconStyle( {
                    src: locationMarker,
                    width: 15
                } ),
                text: new Text( { font: "12px Calibri,sans-serif",
                    overflow: true,
                    fill: new Fill( {
                        color: "#000"
                    } ),
                    offsetX: 10,
                    text: index,
                    stroke: new Stroke( {
                        color: "#fff",
                        width: 3
                    } ) } ),
            } );
        }
    } );
    const lineSource = new VectorSource( {
        features: [ lineFeature ],
    } );

    const lineLayer = new VectorLayer( {
        source: lineSource,
        style: lineStyle
    } );
    useEffect( () =>
    {
        const osmLayer = new TileLayer( {
            preload: Infinity,
            source: new OSM(),
        } );

        const map = new Map( {
            controls: [ ],
            interactions: [],
            target: mapRef?.current ?? undefined,
            layers: [ osmLayer, pointLayer, lineLayer/*, cityLayer*/ ],
            view: new View( {
                center: fromLonLat( [ -75, 35 ] ),
                zoom: 0,
            } ),
        } );
        const extent = pointSource.getExtent();
        if ( isValidExtent( extent ) )
        {
            map.getView().fit( extent, {
                padding: [ 20, 20, 20, 20 ], // Optional padding around the extent
                duration: 0, // Optional animation duration in milliseconds
            } );
        }
        return () => map.setTarget( undefined ); // null
    }, [ trip ] );

    const riskLevel = categoryToRiskLevel( risk ?? RiskCategory.UNRECOGNIZED );
    return (
        <div
            style = {{ height: "110px", width: "100%", position: "relative" }} // make the map container relatively positioned
            ref = {mapRef}
            className = "map-container"
        >
            {/* This will need to be updated to show the max risk encountered, not what they asked for. */}
            {risk !== undefined && (
                <div>
                    <Chip
                        label = {
                            isAdvisory
                                ? ADVISORY_ROUTE_TEXT
                                : <ImpactIcon level = {riskLevel} width = {14} blackAndWhite includeText/>
                        }
                        sx = {{
                            position: "absolute",
                            top: "5px",
                            left: "5px",
                            zIndex: 10,
                            color: isAdvisory
                                ? getContrastColor( ADVISORY_COLOR )
                                : getContrastColor( impactLevelInfo[riskLevel].color ),
                            backgroundColor: isAdvisory
                                ? ADVISORY_COLOR
                                : impactLevelInfo[riskLevel].color,
                        }}
                    />
                </div>
            )}
            {shouldShowGenerateNewTrip && (

                <Chip
                    onClick = {( event ) =>
                    {
                        //stop propagation to prevent the parent div's onclick event from firing
                        event.stopPropagation();
                        const routeRequestInput = trip.routeRequestInput;
                        if ( routeRequestInput !== undefined && isPastStartDate( routeRequestInput ) )
                        {
                            dispatch( openEdit( {
                                trip: trip,
                                routeRequestInput: routeRequestInput,
                                isForecastUpdate: true
                            } ) );

                            const inputWaypoints = routeRequestInput.waypoints;
                            const homeViewState = getRouteBoundingBox( inputWaypoints );
                            if ( inputWaypoints.length > 0 )
                            {
                                dispatch( setSelectionState( { waypoints: inputWaypoints, homeViewState } ) );
                            }
                        }
                        else if ( routeRequestInput !== undefined )
                        {
                            const newRouteRequestInput = resubmitRouteRequestInput( routeRequestInput );
                            void resubmitRequest(
                                trip.Id,
                                trip.name,
                                newRouteRequestInput,
                            );
                            resetToPage( event, 1 );
                        }

                    }}
                    label = {
                        <span>
                            <FontAwesomeIcon icon = {faRotateRight} size = "lg" fixedWidth/>
                            <span>{NEW_FORECAST_MESSAGE}</span>
                        </span>
                    }
                    sx = {{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        zIndex: 10,
                        color: "white",
                        backgroundColor: "rgb(0, 0, 0)",
                    }}
                />

            )}
        </div>
    );
}
